'use client';

import { useState } from 'react';
import { Send, Bot, Loader2, FileText, Check, X, Edit2, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SideBySideDiff } from './diff-preview';
import type { Citation } from '@/lib/ai-client';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Evidence item from AI response
interface EvidenceItem {
  type: 'document' | 'artefact';
  id: string;
  name: string;
  excerpt?: string;
  sectionPath?: string;
}

// Patch suggestion from Ask AI
interface PatchSuggestion {
  patches: Array<{ op: string; path: string; value: unknown }>;
  beforeAfter: { before: string; after: string };
  rationale: string[];
  evidence: EvidenceItem[];
  sectionPath?: string;
}

interface AskAIBarProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (message: string) => Promise<{ response: string; citations: Citation[] } | PatchSuggestion | void>;
  onApply?: (suggestion: PatchSuggestion, editedContent?: string) => Promise<void>;
  onInsertResponse?: (responseText: string) => void;
  placeholder?: string;
  isLoading?: boolean;
  mode?: 'chat' | 'patch'; // 'chat' for simple response, 'patch' for diff/patch mode
}

export function AskAIBar({
  isOpen,
  onClose,
  onSend,
  onApply,
  onInsertResponse,
  placeholder = "E.g., Add more detailed demographics, focus on B2B users, make personas younger...",
  isLoading: externalLoading,
  mode = 'chat',
}: AskAIBarProps) {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [response, setResponse] = useState<{ text: string; citations: Citation[] } | null>(null);
  const [patchSuggestion, setPatchSuggestion] = useState<PatchSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isApplying, setIsApplying] = useState(false);

  const isLoading = externalLoading || isSending;

  if (!isOpen) return null;

  const handleSend = async () => {
    if (!message.trim() || isLoading) return;

    setIsSending(true);
    setError(null);
    setResponse(null);
    setPatchSuggestion(null);

    try {
      const result = await onSend(message);
      if (result) {
        // Check if it's a patch suggestion
        if ('patches' in result && 'beforeAfter' in result) {
          setPatchSuggestion(result as PatchSuggestion);
          setEditedContent((result as PatchSuggestion).beforeAfter.after);
        } else if ('response' in result) {
          setResponse({ text: result.response, citations: result.citations });
        }
      }
      setMessage('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get AI response');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  const handleClose = () => {
    setResponse(null);
    setPatchSuggestion(null);
    setError(null);
    setMessage('');
    setIsEditing(false);
    setEditedContent('');
    onClose();
  };

  const handleAccept = async () => {
    if (!patchSuggestion || !onApply) return;

    setIsApplying(true);
    try {
      await onApply(patchSuggestion, isEditing ? editedContent : undefined);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply changes');
    } finally {
      setIsApplying(false);
    }
  };

  const handleReject = () => {
    setPatchSuggestion(null);
    setIsEditing(false);
    setEditedContent('');
  };

  const handleEdit = () => {
    if (patchSuggestion) {
      setEditedContent(patchSuggestion.beforeAfter.after);
      setIsEditing(true);
    }
  };

  return (
    <div className="bg-indigo-50 border border-indigo-100 rounded-[14px] p-5 mb-4">
      {/* Header */}
      <div className="mb-3">
        <span className="text-sm font-medium text-gray-800">
          {patchSuggestion ? 'AI Suggestion' : response ? 'AI Response' : 'What would you like to change or improve?'}
        </span>
      </div>

      {/* Patch Suggestion Display */}
      {patchSuggestion && !isEditing && (
        <div className="mb-4 space-y-4">
          {/* Diff View */}
          <SideBySideDiff
            before={patchSuggestion.beforeAfter.before}
            after={patchSuggestion.beforeAfter.after}
          />

          {/* Rationale */}
          {patchSuggestion.rationale.length > 0 && (
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs font-medium text-gray-500 mb-2">Why this change?</p>
              <ul className="space-y-1">
                {patchSuggestion.rationale.map((reason, index) => (
                  <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                    <span className="text-brand-primary mt-0.5">•</span>
                    {reason}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Evidence */}
          {patchSuggestion.evidence.length > 0 && (
            <div className="p-3 bg-white rounded-lg border border-gray-200">
              <p className="text-xs font-medium text-gray-500 mb-2">Evidence</p>
              <div className="flex flex-wrap gap-2">
                {patchSuggestion.evidence.map((item, index) => (
                  <div
                    key={index}
                    className={cn(
                      "flex items-center gap-1.5 px-2 py-1 rounded text-xs",
                      item.type === 'document'
                        ? "bg-gray-100 text-gray-700"
                        : "bg-brand-primary/10 text-brand-primary"
                    )}
                    title={item.excerpt}
                  >
                    {item.type === 'document' ? (
                      <FileText className="w-3 h-3" />
                    ) : (
                      <Users className="w-3 h-3" />
                    )}
                    <span className="truncate max-w-[150px]">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleAccept}
              disabled={isApplying}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isApplying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">Accept</span>
            </button>
            <button
              onClick={handleReject}
              disabled={isApplying}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              <X className="w-4 h-4" />
              <span className="text-sm font-medium">Reject</span>
            </button>
            <button
              onClick={handleEdit}
              disabled={isApplying}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              <Edit2 className="w-4 h-4" />
              <span className="text-sm font-medium">Edit</span>
            </button>
          </div>
        </div>
      )}

      {/* Edit Mode */}
      {isEditing && patchSuggestion && (
        <div className="mb-4 space-y-4">
          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-blue-50 px-3 py-2 border-b border-blue-200">
              <span className="text-xs font-medium text-blue-700">Edit Suggestion</span>
            </div>
            <textarea
              value={editedContent}
              onChange={(e) => setEditedContent(e.target.value)}
              className="w-full p-3 text-sm text-gray-700 min-h-[150px] focus:outline-none"
              placeholder="Edit the suggestion..."
            />
          </div>

          {/* Edit Action Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleAccept}
              disabled={isApplying}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isApplying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">Apply Edited</span>
            </button>
            <button
              onClick={() => setIsEditing(false)}
              disabled={isApplying}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50"
            >
              <span className="text-sm font-medium">Cancel Edit</span>
            </button>
          </div>
        </div>
      )}

      {/* Response Display (for chat mode) */}
      {response && mode === 'chat' && (
        <div className="mb-4 p-4 bg-white rounded-[10px] border border-gray-200">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4 text-brand-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-gray-900">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children }) => <h1 className="text-base font-semibold mb-2">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-sm font-semibold mb-2">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-sm font-semibold mb-1">{children}</h3>,
                    p: ({ children }) => <p className="text-sm text-gray-900 whitespace-pre-wrap mb-2">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="text-sm text-gray-900">{children}</li>,
                    code: ({ children }) => (
                      <code className="px-1 py-0.5 rounded bg-gray-100 text-gray-800">{children}</code>
                    ),
                    pre: ({ children }) => (
                      <pre className="p-3 rounded bg-gray-50 border border-gray-200 overflow-x-auto mb-2">{children}</pre>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-2 border-gray-300 pl-3 text-gray-700 mb-2">{children}</blockquote>
                    ),
                    table: ({ children }) => (
                      <div className="overflow-x-auto mb-2">
                        <table className="min-w-full border-collapse">{children}</table>
                      </div>
                    ),
                    th: ({ children }) => (
                      <th className="border border-gray-200 bg-gray-50 px-2 py-1 text-left text-xs font-semibold text-gray-700">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="border border-gray-200 px-2 py-1 text-xs text-gray-700">{children}</td>
                    ),
                  }}
                >
                  {response.text}
                </ReactMarkdown>
              </div>

              {/* Citations */}
              {response.citations.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-medium text-gray-500 mb-2">Sources:</p>
                  <div className="flex flex-wrap gap-2">
                    {response.citations.slice(0, 3).map((citation, index) => (
                      <div
                        key={citation.chunkId || index}
                        className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 border border-gray-200 rounded text-xs text-gray-600"
                        title={citation.content}
                      >
                        <FileText className="w-3 h-3" />
                        <span className="truncate max-w-[150px]">{citation.documentName}</span>
                        {citation.page && <span className="text-gray-400">p.{citation.page}</span>}
                      </div>
                    ))}
                    {response.citations.length > 3 && (
                      <span className="text-xs text-gray-400">
                        +{response.citations.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {onInsertResponse && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <button
                    onClick={() => onInsertResponse(response.text)}
                    className="px-3 py-1.5 text-sm bg-brand-primary text-white rounded-md hover:bg-brand-primary/90 transition-colors"
                  >
                    Insert into Overview
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-[10px]">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Input Area */}
      {!patchSuggestion && (
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={response ? "Ask a follow-up question..." : placeholder}
            autoFocus
            disabled={isLoading}
            className="flex-1 h-[44px] px-4 border border-gray-200 rounded-[10px] bg-white text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary disabled:bg-gray-50 disabled:cursor-not-allowed"
          />
          <button
            onClick={handleSend}
            disabled={!message.trim() || isLoading}
            className={cn(
              "h-[44px] px-5 flex items-center gap-2 rounded-[10px] transition-all text-white font-medium",
              message.trim() && !isLoading
                ? "bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 shadow-sm"
                : "bg-indigo-300 cursor-not-allowed"
            )}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            <span className="text-sm">{isLoading ? 'Thinking...' : 'Send'}</span>
          </button>
        </div>
      )}
    </div>
  );
}
